import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as queryTypesApi from './query-types.api';

import type {
  CreateQueryTypeRequest,
  QueryTypeListQuery,
  UpdateQueryTypeRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QueryTypeListQuery) => ['query-types', 'list', query] as const,
  detail: (id: string) => ['query-types', 'detail', id] as const,
};

export const useQueryTypesList = (query: QueryTypeListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => queryTypesApi.listQueryTypes(query),
    staleTime: 30_000,
  });

export const useCreateQueryType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQueryTypeRequest) => queryTypesApi.createQueryType(payload),
    onSuccess: (qt) => {
      qc.invalidateQueries({ queryKey: ['query-types', 'list'] });
      toast.success(`Added ${qt.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query type'),
  });
};

export const useUpdateQueryType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQueryTypeRequest }) =>
      queryTypesApi.updateQueryType(id, payload),
    onSuccess: (qt) => {
      qc.invalidateQueries({ queryKey: ['query-types', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(qt.id) });
      toast.success('Query type updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update query type'),
  });
};

export const useDeleteQueryType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => queryTypesApi.deleteQueryType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-types', 'list'] });
      toast.success('Query type deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query type'),
  });
};

export const useBulkDeleteQueryTypes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => queryTypesApi.bulkDeleteQueryTypes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-types', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query types'),
  });
};

export const useBulkSetQueryTypesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      queryTypesApi.bulkSetQueryTypesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-types', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query type status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
